import React, { useState, useEffect } from "react";
import { Typography, Box } from "@mui/material";
import tenantService from "./api/tenantApi";
import dashboardPreferencesService from "./api/tenantApi";
import { toast } from "react-toastify";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import { useSelector } from "react-redux";
import { SegmentProvider } from './context/segmentContext';     
import { getUserInfoBySubId } from './api/tenantApi';

// Import components
import AccountInfo from './components/settings/AccountInfo';
import NotificationSettings from './components/settings/NotificationSettings';
import SegmentManager from './components/settings/SegmentManager';
import OtherServices from './components/settings/OtherServices';
import { getCurrentUserId } from "./authUtils";
import { parsePreferencesField } from "./utils/parsePreferencesField";


const SettingsPage = ({ openOthers }) => {
  // Core state
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [currentCounter, setCurrentCounter] = useState(0);
  const [maxCounterLimit, setMaxCounterLimit] = useState(0);

  // Section expansion state
  const [expandedSections, setExpandedSections] = useState({
    accountInfo: false,
    notifications: false,
    segments: false,
    otherServices: openOthers || false
  });

  // Data state
  const [notificationPreferences, setNotificationPreferences] = useState({});

  // Auth state from Redux
  const isAdmin = useSelector(state => state.userInfo.isAdmin);
  const URLTenant = getTenantIdFromURL();
  const currentTenant = useSelector(state => state.userInfo.tenantId);

  useEffect(() => {
    if (isAdmin) {
      setTenantId(URLTenant);
    } else {
      setTenantId(currentTenant);
    }
  }, [isAdmin, URLTenant, currentTenant]);

  const fetchUser = async () => {
    try {
      const subId = localStorage.getItem("subId");
      const userInfo   = await getUserInfoBySubId(subId);
      setUser(userInfo);
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error("Failed to fetch user details");
    }
  };

  const fetchTenantInfo = async () => {
    if (!tenantId) return;
    try {
      const response = await tenantService.getTenantById(tenantId);
      console.log("🚀 ~ fetchTenantInfo ~ response:", response)
      
      const tenant = response?.data
      setCurrentCounter(tenant?.counter || 0);
      setMaxCounterLimit(tenant?.maxCounterLimit || 10);
    } catch (error) {
      console.error("Error fetching tenant:", error);
      toast.error("Failed to fetch tenant information");
    }
  };

  const fetchNotificationPreferences = async () => {
    if (!tenantId) return;
    // Each fetch is independent — a brand-new user simply has no preferences yet,
    // so a missing/failed one falls back to defaults instead of failing them all.
    const safe = (p) => Promise.resolve(p).catch(() => null);
    try {
      const [barPrefs, emailPrefs, slackPrefs, weeklyPrefs] = await Promise.all([
        safe(dashboardPreferencesService.getUserPreferencesByUserIdAndFeature(getCurrentUserId(), "Notification Preferences")),
        safe(dashboardPreferencesService.getUserPreferencesByUserIdAndFeature(getCurrentUserId(), "Email Notification Preferences")),
        safe(dashboardPreferencesService.getPreferencesByTenantIdAndFeature(tenantId, "Slack Notification Preferences")),
        safe(dashboardPreferencesService.getUserPreferencesByUserIdAndFeature(getCurrentUserId(), "Weekly Notification Preferences")),
      ]);


      // Log raw preferences data
      console.log('Raw Bar Preferences:', barPrefs?.data?.preferences);
      console.log('Raw Email Preferences:', emailPrefs?.data?.preferences);
      console.log('Raw Slack Preferences:', slackPrefs?.data?.preferences);
      console.log('Raw Weekly Preferences:', weeklyPrefs?.data?.preferences);

      const preferences = {
        inApp: parsePreferencesField(
          barPrefs?.data?.preferences ?? barPrefs?.preferences
        ),
        email: parsePreferencesField(
          emailPrefs?.data?.preferences ?? emailPrefs?.preferences
        ),
        slack: parsePreferencesField(
          slackPrefs?.data?.preferences ?? slackPrefs?.preferences
        ),
        weekly: parsePreferencesField(
          weeklyPrefs?.data?.preferences ?? weeklyPrefs?.preferences
        ),
      };
      // Log parsed preferences
      console.log('Parsed Preferences:', preferences);

      setNotificationPreferences(preferences);
    } catch (error) {
      console.error("Error fetching preferences:", error);
      toast.error("Failed to fetch notification preferences");
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      await Promise.all([
        fetchUser(),
        fetchTenantInfo(),
        fetchNotificationPreferences(),
      ]);
      setLoading(false);
    };

    if (tenantId) {
      initializeData();
    }
  }, [tenantId]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h5">Loading Settings...</Typography>
      </Box>
    );
  }

  const renderSectionHeader = (title, section) => (
    <div
      onClick={() => toggleSection(section)}
      style={{
        background: '#5186EC1A',
        padding: '15px 20px',
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        marginBottom: expandedSections[section] ? 0 : '20px',
        boxShadow: expandedSections[section] 
          ? '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)'
          : '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 10px 15px -3px rgba(0, 0, 0, 0.1)'
      }}
    >
      <Typography variant="h6">{title}</Typography>
      <img
        src={`${process.env.PUBLIC_URL}/${expandedSections[section] ? 'Group 7232.svg' : 'Group 7231.svg'}`}
        alt={expandedSections[section] ? "Collapse" : "Expand"}
        width="32"
        height="32"
      />
    </div>
  );
 
  return (
    <div style={{
      padding: '20px',
      background: '#FAFAFC',
      minHeight: '100vh',
      borderRadius: "10px"
    }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 4, color: "#1A2A56" }}>
        Change your profile and account settings
      </Typography>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {!isAdmin && <section style={{ marginBottom: '20px' }}>
          {renderSectionHeader('Account-General Info', 'accountInfo')}
          <AccountInfo
            user={user}
            isExpanded={expandedSections.accountInfo}
          />
        </section>}

        <section style={{ marginBottom: '20px' }}>
          {renderSectionHeader('Notifications', 'notifications')}
          <NotificationSettings
            isExpanded={expandedSections.notifications}
            notificationPreferences={notificationPreferences}
            onUpdatePreferences={async (newPrefs) => {
              setNotificationPreferences(newPrefs);
            }}
            tenantId={tenantId}
          />
        </section>

        {/* Wrap only the SegmentManager with SegmentProvider */}
        <SegmentProvider>
          <section style={{ marginBottom: '20px' }}>
            {renderSectionHeader('Segments', 'segments')}
            <SegmentManager
              isExpanded={expandedSections.segments}
              tenantId={tenantId}
            />
          </section>
        </SegmentProvider>

        <section style={{ marginBottom: '20px' }}>
          {renderSectionHeader('Other Services', 'otherServices')}
          <OtherServices
            isExpanded={expandedSections.otherServices}
            user={user}
            tenantId={tenantId}
            currentCounter={currentCounter}
            maxCounterLimit={maxCounterLimit}
          />
        </section>
      </div>
    </div>
  );
};

export default SettingsPage;
